import { TDeviceType, useDeviceType } from "@cian/ui-kit";
import * as React from 'react';
import { connect } from 'react-redux';
import { ca } from '@cian/analytics';
import { RouteComponentProps, useLocation } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { IAnalytics } from "../../types/analytics";
import { IApplicationState, IThunkContext, TReduxActions } from "../../types/redux";

import { IUser } from '../../types/user';
import { IPosts } from '../../types/posts';
import { IGlobalErrorState } from '../../types/global_error';
import { IBreadItem } from '../../types/bread_crumb';
import { getNews, setCategory } from '../../reducers/modules/news/news';
import { List } from '../../components/list/list';
import { LoadError } from '../../components/load_error/load_error';
import { BreadCrumbs } from '../bread_crumbs/bread_crumbs';
import { Aside } from '../aside/aside';
import { setBreadCrumb } from '../../reducers/modules/bread_crumbs/bread_crumbs';
import { saveScroll } from '../../reducers/modules/scroll/scroll';
import { IScroll } from '../../types/scroll';
import { getDiscussed } from '../../reducers/modules/discussed/discussed';
import { ListItem } from '../../components/list_item/list_item';
import { scrollComments } from '../../reducers/modules/scroll_comment/scroll_comment';
import { IConfig } from '../../types/config';
import { combineDataLayerObject } from '../../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../../app/helpers/breadcrumbs_helper';

import s from './news.css';

interface IProps {
  user: IUser;
  news: IPosts;
  scroll: IScroll;
  globalError: IGlobalErrorState;
  config: IConfig;
  deviceType: TDeviceType;
  analytics: IAnalytics;
}

interface INewsProps extends IProps {
  routeCategory: string;
}

interface IDispatchProps {
  getNews(params: any): void;
  setCategory(category: string): void;
  setBreadCrumb(path: IBreadItem[]): void;
  getDiscussed(regionID: string): void;
  saveScroll(item: any): void;
  scrollComments(): void;
}

export const setCrumbsNews = (tag: string, search: string, category: string) => {
  const crumbs: IBreadItem[] = [
    {path: '/magazine'},
    {path: '/novosti'},
  ];

  if (!!tag) {
    const name = tag[0].toUpperCase() + tag.substr(1);

    crumbs.push({
      path: tag,
      url: `/novosti?tag=${tag}`,
      name,
    });
  } else if (!!search) {
    crumbs.push({
      path: search,
      url: `/novosti?search=${search}`,
      name: 'Поиск',
    });
  } else if (!!category) {
    crumbs.push({path: category});
  }

  return crumbs;
}

const NewsContainer: React.FC<INewsProps & IDispatchProps & RouteComponentProps> = props => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tag = query.get("tag") || '';
  const search = query.get("search") || '';
  const ordering = query.get("ordering") || '-date_publish';

  const {config, routeCategory} = props;
  const deviceType = useDeviceType();
  const page = query.get("page") ? parseInt(query.get("page") as string) : 1;
  const regionID = config.regionID && config.regionID !== '0' ? config.regionID : '';
  const category = routeCategory ? routeCategory : query.get("category") || '';

  React.useEffect(() => {
    const params = { page, ordering, tag, category, search, regionID };

    const crumbs = setCrumbsNews(tag, search, category);

    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(props.analytics, getBreadCrumbsNames(crumbs), deviceType));
    }

    props.getNews(params);
    props.setBreadCrumb(crumbs);
    props.getDiscussed(regionID);
    props.setCategory(category);
  }, [tag, search, ordering, page, regionID, category]);

  function loadMore(pageN: number) {
    const page = pageN || 1;
    const category = props.news.currentCategory || '';
    const regionID = props.config.regionID && props.config.regionID !== '0' ? props.config.regionID : '';
    const params = { page, ordering, tag, category, search, regionID };

    return props.getNews(params);
  }

  function seoHideBlock() {
    return (
      <span className={s['news__schema']}>
        <span itemProp="headline">ЦИАН - новости рынка недвижимости Москвы и России</span>
        <span itemProp="description">
          ЦИАН - новости рынка недвижимости в России.
          Новости об аренде и продаже квартир, офисов, домов и другой недвижимости.
          Новости о новостройках в вашем регионе.
        </span>
      </span>
    );
  }

  const {
    items,
    pages_count: pagesCount,
    count: recordsCount,
  } = props.news;
  const { item: selectedItem } = props.scroll;

  return (
    <div className={s['news-wrap']}>
      <div itemScope itemType="https://schema.org/NewsArticle" className={`${s['news']} cg-col-16 cg-col-xs-16`}>
        {seoHideBlock()}
        <BreadCrumbs />
        {
          props.globalError.error ?
            <LoadError /> :
            <List
                currentUser={props.user}
                items={items}
                pagesCount={pagesCount}
                recordsCount={recordsCount}
                searchTag={tag}
                selectedItem={selectedItem}
                saveScroll={props.saveScroll}
                listItemClass={ListItem}
                scrollComments={props.scrollComments}
                search={search}
                onLoadMore={(page: number) => loadMore(page)} />
        }
      </div>
      <Aside />
    </div>
  );
}

function mapStateToProps(state: IApplicationState): IProps {
  const {
    news,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  } = state;

  return {
    news,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch<IApplicationState, IThunkContext, TReduxActions>): IDispatchProps {
  return {
    getNews: (params: any) => {
      dispatch(getNews(params));
    },
    setCategory: (category: string) => {
      dispatch(setCategory(category));
    },
    setBreadCrumb: (path: IBreadItem[]) => {
      dispatch(setBreadCrumb(path));
    },
    getDiscussed: (regionID: string) => {
      dispatch(getDiscussed(regionID));
    },
    saveScroll: (item: any) => {
      dispatch(saveScroll(item));
    },
    scrollComments: () => {
      dispatch(scrollComments());
    },
  }
}

const mergePropsMarket = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): INewsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'market'}, stateProps, dispatchProps, ownProps)
}

const mergePropsCompany = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): INewsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'company'}, stateProps, dispatchProps, ownProps)
}

export const News = connect(mapStateToProps, mapDispatchToProps)(NewsContainer);
export const MarketNews = connect(mapStateToProps, mapDispatchToProps, mergePropsMarket)(NewsContainer);
export const CompanyNews = connect(mapStateToProps, mapDispatchToProps, mergePropsCompany)(NewsContainer);
